/**
 * Maps the real-world clock hour onto a shift's list of hour slots
 * (e.g. ["7-8", "8-9", ...] for day, wrapping past midnight for night).
 *
 * Returns the index of the in-progress ("live") slot, or -1 when there is none:
 * either the displayed shift is not the latest shift, or the current time falls
 * outside the shift window. The slot whose start hour equals the current clock
 * hour is considered live.
 */
export function resolveLiveHourIndex(
  hours: string[],
  currentClockHour: number | null,
  isLatestShift: boolean
): number {
  if (!isLatestShift || currentClockHour == null) return -1;

  return hours.findIndex(slot => parseInt(slot.split("-")[0], 10) === currentClockHour);
}

/** True when the hour slot at `index` is later than the live slot and therefore not yet reached. */
export function isHourSlotInFuture(index: number, liveHourIndex: number): boolean {
  return liveHourIndex >= 0 && index > liveHourIndex;
}

// --- Index-based shift hours (used by the Production truck-loads hour selector,
// which numbers a shift's hours 1..12 rather than labelling clock ranges) ---
//
// Note: these take the shift's start hour and length as arguments rather than deriving them from a
// DAY/NIGHT flag. They used to hardcode 07:00 and 19:00, which disagreed with this deployment's
// configured bands of 06:00 and 18:00 and put every hour bucket an hour out. Callers pass
// shiftStartHour()/shiftDurationHours() from offline/shiftBands, which read the bands the API serves
// from appsettings, so there is one source of truth and it is the configuration.

/** 1-based hour index within the shift for a given wall-clock hour (wraps past midnight). */
export function hourIndexFromClockHour(clockHour: number, shiftStartClockHour: number): number {
  return ((clockHour - shiftStartClockHour + 24) % 24) + 1;
}

/** Wall-clock start hour (0-23) for a 1-based shift hour index. */
export function clockHourFromHourIndex(index: number, shiftStartClockHour: number): number {
  return (shiftStartClockHour + (index - 1)) % 24;
}

/**
 * The in-progress ("live") hour index for the latest shift, or null when there is none: not the
 * latest shift, unknown time, or the current time falls outside the shift's window.
 */
export function resolveLiveShiftHourIndex(
  clockHour: number | null,
  shiftStartClockHour: number,
  shiftDurationHours: number,
  isLatestShift: boolean
): number | null {
  if (!isLatestShift || clockHour == null) return null;
  const index = hourIndexFromClockHour(clockHour, shiftStartClockHour);
  return index >= 1 && index <= shiftDurationHours ? index : null;
}

/**
 * The shift's hour slots as "start-end" labels, e.g. ["6-7", "7-8", ...], wrapping past midnight.
 * Callers pass the configured start hour and length rather than a shift letter - the Ore Tally table
 * built these inline from a hardcoded 7..19, which is an hour out against bands of 06:00/18:00 and
 * left the shift's first hour off the picker while offering one past the end.
 */
export function shiftHourSlots(shiftStartClockHour: number, shiftDurationHours: number): string[] {
  const slots: string[] = [];
  for (let i = 0; i < shiftDurationHours; i++) {
    const start = (shiftStartClockHour + i) % 24;
    slots.push(`${start}-${(start + 1) % 24}`);
  }
  return slots;
}
